import { AlgoliaProductFetcher } from '@lyle/algolia-product-fetcher';
import {
  hiddenSalePrice,
  isLightSwatchColour,
  isSwatchSoldOut,
  isWhiteSwatch,
  metafieldValue,
} from '@lyle/algolia-product-utils';
import {
  bindProductColourNav,
  getColourFamilyCache,
  setColourFamilyCache,
} from '@lyle/product-colour-nav';

class LyleColourSwatches extends HTMLElement {
  #abortController = null;
  #formatter = null;
  #products = [];
  #currentHandle = '';
  #config = null;
  #selectedOptionValues = [];
  #variantHandler = null;
  #sizeClickHandler = null;

  connectedCallback() {
    const familyTag = this.dataset.familyTag;
    if (!familyTag) return;

    bindProductColourNav();
    this.#abortController = new AbortController();

    this.#variantHandler = (e) => {
      this.#selectedOptionValues = e.detail?.optionValues ?? [];
      this.#refreshSoldOutState();
    };
    document.addEventListener('product:variant-change', this.#variantHandler);

    this.#sizeClickHandler = (e) => {
      const btn = e.target.closest('[data-size-btn]');
      if (!btn || btn.disabled) return;
      // Size button sets selection synchronously; read on next task so
      // lyle-main-product can apply data-selected / fire variant-change first.
      queueMicrotask(() => {
        this.#selectedOptionValues = this.#readSelectedOptionValues();
        this.#refreshSoldOutState();
      });
    };
    document.addEventListener('click', this.#sizeClickHandler);

    this.#selectedOptionValues = this.#readSelectedOptionValues();

    const cached = getColourFamilyCache(familyTag);
    if (cached?.length) {
      this.#renderFromProducts(cached).catch(() => {});
    }

    this.#fetchAndRender(familyTag)
      .catch(() => {
        this.querySelector('[data-swatches-container]')?.remove();
      })
      .finally(() => this.#markResolved());
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#formatter = null;
    this.#products = [];

    if (this.#variantHandler) {
      document.removeEventListener('product:variant-change', this.#variantHandler);
      this.#variantHandler = null;
    }

    if (this.#sizeClickHandler) {
      document.removeEventListener('click', this.#sizeClickHandler);
      this.#sizeClickHandler = null;
    }
  }

  async #fetchAndRender(familyTag) {
    const config = this.#parseJson('[data-colour-swatches-config]');
    const productData = this.#parseJson('[data-colour-swatches-product]');

    if (!config || !productData) return;

    const { signal } = this.#abortController;
    let related = await this.#fetchThemeProducts(familyTag, config);

    if (!related.length) {
      const fetcher = new AlgoliaProductFetcher({ config, signal });
      const productsByTag = await fetcher.fetchRelatedProductsBatch([familyTag]);
      related = productsByTag.get(familyTag) || [];
    }

    if (signal.aborted) return;

    setColourFamilyCache(familyTag, related);
    await this.#renderFromProducts(related, config, productData);
  }

  async #fetchThemeProducts(familyTag, config) {
    try {
      const params = new URLSearchParams({ section_id: 'colour-swatches-data' });
      const rootUrl = config.market.rootUrl || '/';
      const response = await fetch(
        `${rootUrl}collections/all/${encodeURIComponent(familyTag)}?${params}`,
        {
          signal: this.#abortController.signal,
        },
      );

      if (!response.ok) return [];

      const html = await response.text();
      const document = new DOMParser().parseFromString(html, 'text/html');
      const data = document.querySelector('[data-colour-swatches-products]');
      return data ? JSON.parse(data.textContent) : [];
    } catch {
      return [];
    }
  }

  async #renderFromProducts(related, config, productData) {
    const resolvedConfig = config ?? this.#parseJson('[data-colour-swatches-config]');
    const resolvedProduct =
      productData ?? this.#parseJson('[data-colour-swatches-product]');

    if (!resolvedConfig || !resolvedProduct) return;

    this.#config = resolvedConfig;
    this.#currentHandle = resolvedProduct.handle;

    const colourFamily = resolvedProduct.tags?.find((t) =>
      t.startsWith('ProductColourFamily-'),
    );

    const allColourProducts = related.filter(
      (p) =>
        (!colourFamily || p.tags?.includes(colourFamily)) &&
        Boolean(metafieldValue(p, 'swatch_code')),
    );

    const container = this.querySelector('[data-swatches-container]');
    if (!container) return;

    if (!allColourProducts.length) {
      container.remove();
      return;
    }

    document.dispatchEvent(
      new CustomEvent('colour-swatches:siblings', {
        bubbles: false,
        detail: {
          products: allColourProducts,
          currentHandle: resolvedProduct.handle,
          config: resolvedConfig,
        },
      }),
    );

    const sizeTag = resolvedProduct.tags?.find(
      (t) => t.startsWith('ProductSize-') && !t.startsWith('ProductSizeFamily-'),
    );
    const swatchProducts = sizeTag
      ? allColourProducts.filter((p) => p.tags?.includes(sizeTag))
      : allColourProducts;

    if (!swatchProducts.length) {
      container.remove();
      return;
    }

    this.#products = swatchProducts.map((product) => {
      if (
        product.handle === resolvedProduct.handle &&
        typeof resolvedProduct.available === 'boolean'
      ) {
        return { ...product, available: resolvedProduct.available };
      }
      return product;
    });
    this.#selectedOptionValues = this.#readSelectedOptionValues();
    this.#updateSubtitle(resolvedProduct.handle);
    const groups = this.#groupByPriceTier(
      this.#products,
      Boolean(resolvedProduct.isCustomer),
    );
    container.classList.remove('colour-swatches__swatches--loading');
    this.#renderGroups(groups, resolvedProduct.handle, container, resolvedConfig);
  }

  #groupByPriceTier(products, isCustomer) {
    const groups = new Map();

    for (const product of products) {
      const priceKey = String(product.price?.min ?? product.price?.amount ?? 0);
      const salePrice = hiddenSalePrice(product.tags, this.#config.market.currencyCode);
      const memberDiscount = isCustomer
        ? LyleColourSwatches.#memberDiscount(product)
        : 0;
      // Colours sharing a list price but not a member discount resolve to
      // different member prices, so they cannot share a price label.
      const key = `${priceKey}|${memberDiscount}|${salePrice}`;

      if (!groups.has(key)) {
        groups.set(key, {
          price: product.price,
          compareAtPrice: product.compareAtPrice,
          hiddenSalePrice: salePrice,
          memberDiscount,
          priceKey,
          products: [],
        });
      }

      const group = groups.get(key);
      if (!group.products.find((p) => p.handle === product.handle)) {
        group.products.push(product);
      }
    }

    return new Map(
      [...groups.entries()].sort(
        (a, b) => Number(b[1].priceKey) - Number(a[1].priceKey),
      ),
    );
  }

  #renderGroups(groups, currentHandle, container, config) {
    const entries = [...groups.values()];
    this.querySelector('[data-colour-price]')?.toggleAttribute('hidden', entries.length > 1);

    if (entries.length <= 1) {
      const group = entries[0];
      container.innerHTML = `<div class="colour-swatches__group-swatches">${(group?.products ?? [])
        .map((p) => this.#swatchHtml(p, currentHandle))
        .join('')}</div>`;
    } else {
      container.innerHTML = entries
        .map((group) => {
          const priceLabelHtml = this.#priceLabelHtml(group, config);
          return `<div class="colour-swatches__price-group">
            <div class="colour-swatches__price-label text-body-sm-bold">${priceLabelHtml}</div>
            <div class="colour-swatches__group-swatches">
              ${group.products.map((p) => this.#swatchHtml(p, currentHandle)).join('')}
            </div>
          </div>`;
        })
        .join('');
    }

    this.#markResolved();
  }

  // The server-rendered badge stays suppressed until the price groups are known,
  // otherwise it paints in the price row and then moves into a group label.
  #markResolved() {
    this.setAttribute('data-groups-resolved', '');
  }

  #priceLabelHtml(group, config) {
    const memberDiscount = group.memberDiscount;
    const compareAt = group.compareAtPrice?.amount;
    const price = group.price?.amount;
    const onSale = compareAt && price && compareAt > price;

    if (group.hiddenSalePrice !== null) {
      const original = price - price * memberDiscount;
      const compare = original > group.hiddenSalePrice
        ? `<span class="colour-swatches__compare-at-price">${LyleColourSwatches.#esc(this.#formatPrice({ ...group.price, amount: original }, config))}</span>`
        : '';
      const sale = LyleColourSwatches.#esc(
        this.#formatPrice({ ...group.price, amount: group.hiddenSalePrice }, config),
      );
      const priceClass = original > group.hiddenSalePrice ? 'colour-swatches__sale-price' : '';
      return `${compare}<span class="${priceClass}">${sale}</span>${memberDiscount > 0 ? this.#memberBadgeHtml() : ''}`;
    }

    if (memberDiscount > 0 && price) {
      const discounted = price - price * memberDiscount;
      // The previous price stays the full price, not the sale price the member
      // discount comes off.
      const compareFormatted = LyleColourSwatches.#esc(
        this.#formatPrice(onSale ? group.compareAtPrice : group.price, config),
      );
      const memberFormatted = LyleColourSwatches.#esc(
        this.#formatPrice(
          { ...group.price, amount: discounted },
          config,
        ),
      );
      const badge = this.#memberBadgeHtml();
      return `<span class="colour-swatches__compare-at-price">${compareFormatted}</span><span class="colour-swatches__member-price">${memberFormatted}</span>${badge}`;
    }

    if (onSale) {
      const compareFormatted = LyleColourSwatches.#esc(
        this.#formatPrice(group.compareAtPrice, config),
      );
      const priceFormatted = LyleColourSwatches.#esc(
        this.#formatPrice(group.price, config),
      );
      return `<span class="colour-swatches__compare-at-price">${compareFormatted}</span><span class="colour-swatches__sale-price">${priceFormatted}</span>`;
    }

    return LyleColourSwatches.#esc(this.#formatPrice(group.price, config));
  }

  #memberBadgeHtml() {
    const template = this.querySelector(
      '[data-colour-price] [data-member-price-badge]',
    );
    if (!template) return '';

    const badge = template.cloneNode(true);
    badge.removeAttribute('hidden');
    // Only the template badge tracks the selected variant in lyle-main-product.
    badge.removeAttribute('data-member-price-badge');
    return badge.outerHTML;
  }

  #swatchHtml(product, currentHandle) {
    const swatchCode = metafieldValue(product, 'swatch_code');
    const isActive = product.handle === currentHandle;
    const isWhite = isWhiteSwatch(swatchCode);
    const isLight = isLightSwatchColour(swatchCode);
    const soldOut = isSwatchSoldOut(product, this.#selectedOptionValues);

    let classes = 'colour-swatches__swatch';
    if (isActive) classes += ' is-active';
    if (isWhite) classes += ' colour-swatches__swatch--white';
    if (soldOut) classes += ' colour-swatches__swatch--sold-out';
    if (soldOut && isLight) classes += ' colour-swatches__swatch--light';

    const safeSwatchCode = /^[a-zA-Z0-9#(),.% +\-]+$/.test(swatchCode ?? '')
      ? swatchCode
      : '';

    const prefetchImages = (product.images || [])
      .slice(0, 3)
      .map((image) => image.src)
      .filter(Boolean);

    return `<a
      class="${classes}"
      href="${LyleColourSwatches.#esc(product.url)}"
      aria-label="${LyleColourSwatches.#esc(product.title ?? '')}"
      ${isActive ? 'aria-current="page"' : ''}
      ${soldOut ? 'data-sold-out' : ''}
      data-swatch
      data-product-colour-nav
      data-product-handle="${LyleColourSwatches.#esc(product.handle)}"
      data-prefetch-images="${LyleColourSwatches.#esc(JSON.stringify(prefetchImages))}"
      style="--swatch-color: ${LyleColourSwatches.#esc(safeSwatchCode)};"
    ><span class="colour-swatches__swatch-inner"></span></a>`;
  }

  #refreshSoldOutState() {
    for (const product of this.#products) {
      const swatch = this.querySelector(
        `[data-swatch][data-product-handle="${CSS.escape(product.handle)}"]`,
      );
      if (!swatch) continue;

      const swatchCode = metafieldValue(product, 'swatch_code');
      const soldOut = isSwatchSoldOut(product, this.#selectedOptionValues);
      const isLight = isLightSwatchColour(swatchCode);

      swatch.classList.toggle('colour-swatches__swatch--sold-out', soldOut);
      swatch.classList.toggle(
        'colour-swatches__swatch--light',
        soldOut && isLight,
      );
      swatch.toggleAttribute('data-sold-out', soldOut);
    }
  }

  #updateSubtitle(handle) {
    const product = this.#products.find((p) => p.handle === handle);
    const name = metafieldValue(product, 'swatch_name');
    if (!name) return;

    const subtitle = this.querySelector('.colour-swatches__subtitle');
    if (subtitle) subtitle.textContent = name;
  }

  #readSelectedOptionValues() {
    const root = this.closest('lyle-main-product') || document;
    const values = [];

    root.querySelectorAll('[data-size-btn][data-selected]').forEach((btn) => {
      if (btn.dataset.optionValue) values.push(btn.dataset.optionValue);
    });

    if (values.length) return values;

    const hasSizeOption = !!root.querySelector('[data-size-buttons]');
    const hasVariantParam = new URLSearchParams(window.location.search).has('variant');
    if (hasSizeOption && !hasVariantParam) return [];

    root.querySelectorAll('select[data-option-position]').forEach((select) => {
      if (select.value) values.push(select.value);
    });

    return values;
  }

  #formatPrice(price, config) {
    if (!price?.amount) return '';
    if (!this.#formatter) {
      try {
        this.#formatter = new Intl.NumberFormat(
          config?.market?.language || 'en-GB',
          {
            style: 'currency',
            currency: config?.market?.currencyCode || price.currencyCode || 'GBP',
            minimumFractionDigits: 2,
          },
        );
      } catch {
        return String(price.amount);
      }
    }
    return this.#formatter.format(price.amount);
  }

  #parseJson(selector) {
    const script = this.querySelector(selector);
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  static #memberDiscount(product) {
    const raw = Number(metafieldValue(product, 'member_discount_percentage'));
    if (!raw || Number.isNaN(raw)) return 0;
    // Merchants store either a fraction (0.2) or a whole percentage (20).
    return raw > 1 ? raw / 100 : raw;
  }

  static #esc(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

if (!customElements.get('lyle-colour-swatches')) {
  customElements.define('lyle-colour-swatches', LyleColourSwatches);
}
